import React from "react";
import {useNavigate} from "react-router-dom";
import {ContentCategory, ContentStatus} from "../../../../constant/CodeSpecNum";

/** 완료 / 진행 / 초기 / 반려 — 4톤 (브랜드 색 기준) */
const PALETTE = {
    complete: {text: "#2d6a3e", track: "#edf7ed", accent: "#38932d"},
    active: {text: "#133E87", track: "#e4f1fc", accent: "#4393f0"},
    early: {text: "#9a6200", track: "#fef6e8", accent: "#e8a317"},
};

const REJECTED_STYLE = {
    text: "#c43636",
    track: "#fde8e8",
    accent: "#d74b4b",
};

const COMPLETE_NUMERIC = ContentStatus.COMPLETE.statusNumericValue;
const ACTIVE_MIN = ContentStatus.APPROVE.statusNumericValue; // 30 — 결재~

function getStageStyle(progress) {
    const value = Number(progress) || 0;
    if (value >= COMPLETE_NUMERIC) return PALETTE.complete;
    if (value >= ACTIVE_MIN) return PALETTE.active;
    return PALETTE.early;
}

function getStagePercent(statusNumericValue) {
    const value = Number(statusNumericValue) || 0;
    if (value >= COMPLETE_NUMERIC) return 100;
    return Math.min(Math.round((value / COMPLETE_NUMERIC) * 100), 99);
}

function resolveProgressStatus({data, item, row}) {
    if (data && typeof data === "object" && data.statusNumericValue != null) {
        return data;
    }
    const source = row ?? item;
    return source?.contentStatus ?? source?.status ?? null;
}

function formatStatusLabel(status) {
    if (status.rejected === ContentStatus.REJECT.statusValue) {
        const mid = status.statusNumericValue < 90 && status.statusNumericValue > 5 ? " 중" : "";
        return `반려 · ${status.statusKorValue}${mid}`;
    }

    let label = status.statusKorValue ?? "";
    if (status.statusNumericValue === 5) {
        label += " (접수)";
    } else if (
        status.statusNumericValue < 90
        && status.statusNumericValue > 5
        && status.statusNumericValue !== 20
    ) {
        label += " 중";
    }
    return label;
}

function isClickableStatus(status) {
    if (status.contentCategory === ContentCategory.DILIGENCE) {
        return ["접수전", "결재", "진행", "완료"].includes(status.statusKorValue);
    }

    if (status.statusNumericValue == 5) {
        return [
            ContentCategory.INSPECTION,
            ContentCategory.WORK,
            ContentCategory.INCIDENT,
            ContentCategory.INTRODUCTION,
            ContentCategory.DISPOSE,
        ].includes(status.contentCategory);
    }

    return false;
}

function getRatioPalette(percent) {
    if (percent >= 100) return PALETTE.complete;
    if (percent > 0) return PALETTE.active;
    return PALETTE.early;
}

function getRatioProgressMeta(done, total) {
    const completed = Number(done) || 0;
    const count = Number(total) || 0;
    const percent = count <= 0 ? 0 : Math.min(Math.round((completed / count) * 100), 100);

    let label;
    if (percent >= 100) {
        label = count > 0 ? `완료 · ${completed}/${count}` : "완료";
    } else if (percent > 0) {
        label = `진행 중 · ${completed}/${count}`;
    } else {
        label = count > 0 ? `미진행 · 0/${count}` : "미진행";
    }

    return {label, percent, palette: getRatioPalette(percent)};
}

function ProgressBadge({label, percent, palette, clickable, onClick, title}) {
    const tooltip = title ?? `${label} · ${percent}%`;

    return (
        <div
            className={`min-w-0 w-full rounded-md px-2 py-1.5 ${
                clickable ? "cursor-pointer hover:opacity-90" : ""
            }`}
            style={{backgroundColor: palette.track}}
            onClick={clickable ? onClick : undefined}
            title={tooltip}
        >
            <div className="mb-1.5 flex min-w-0 items-center justify-between gap-1">
                <span
                    className="min-w-0 truncate text-[11px] font-semibold leading-tight"
                    style={{color: palette.text}}
                >
                    {label}
                </span>
                <span
                    className="shrink-0 text-[10px] font-medium tabular-nums leading-tight"
                    style={{color: palette.accent}}
                >
                    {percent}%
                </span>
            </div>
            <div className="h-[5px] w-full overflow-hidden rounded-full bg-white/70">
                <div
                    className="h-full rounded-full transition-[width] duration-300 ease-out"
                    style={{
                        width: `${percent}%`,
                        backgroundColor: palette.accent,
                    }}
                />
            </div>
        </div>
    );
}

function ProgressStatusBadge({status, onClick, clickable}) {
    const isRejected = status.rejected === ContentStatus.REJECT.statusValue;
    const palette = isRejected ? REJECTED_STYLE : getStageStyle(status.statusNumericValue);
    const label = formatStatusLabel(status);
    const percent = isRejected ? 100 : getStagePercent(status.statusNumericValue);

    return (
        <ProgressBadge
            label={label}
            percent={percent}
            palette={palette}
            clickable={clickable}
            onClick={onClick}
        />
    );
}

function ProgressStatusHandleBtn({keys, data, item, row}) {
    const navigate = useNavigate();
    const status = resolveProgressStatus({data, item, row});

    if (!status) {
        return null;
    }

    const clickable = isClickableStatus(status);

    const handleClick = (e) => {
        const type = {
            접수전: "",
            결재: "",
            진행: "/handle",
            완료: "",
        };

        if (status.contentCategory === ContentCategory.DILIGENCE) {
            e.stopPropagation();
            navigate(`${keys}${type[status.statusKorValue]}`);
        }

        if (
            status.statusNumericValue == 5
            && (
                status.contentCategory === ContentCategory.INSPECTION
                || status.contentCategory === ContentCategory.WORK
                || status.contentCategory === ContentCategory.INCIDENT
            )
        ) {
            e.stopPropagation();
            navigate(`/maintenance/${status.contentCategory.toLowerCase()}/update/${keys}`);
        }

        if (
            status.statusNumericValue == 5
            && (
                status.contentCategory === ContentCategory.INTRODUCTION
                || status.contentCategory === ContentCategory.DISPOSE
            )
        ) {
            e.stopPropagation();
            navigate(`/lifecycle/${status.contentCategory.toLowerCase()}/update/${keys}`);
        }
    };

    return (
        <div className="flex min-w-0 w-full items-center py-0">
            <ProgressStatusBadge
                status={status}
                clickable={clickable}
                onClick={handleClick}
            />
        </div>
    );
}

export default ProgressStatusHandleBtn;
export {ProgressBadge, getRatioProgressMeta};
